/**
 * Fetch an M3U playlist with a fallback chain:
 *   1. Direct URL
 *   2. Our own /proxy as CORS fallback
 *
 * Each attempt has a 15s timeout. The first valid response
 * (contains #EXTINF) is returned as text.
 */
const M3U_PROXIES: { label: string; build: (u: string) => string }[] = [
  { label: 'direct', build: (u) => u },
  { label: '/proxy', build: (u) => `/proxy?url=${encodeURIComponent(u)}` },
];

export async function fetchM3U(url: string, signal?: AbortSignal): Promise<string> {
  let lastError: Error | null = null;

  for (const { label, build } of M3U_PROXIES) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15_000);

    try {
      const proxyUrl = build(url);
      // Combine external abort signal with our timeout controller
      const combinedSignal = signal
        ? combineAbortSignals(signal, controller.signal)
        : controller.signal;

      const response = await fetch(proxyUrl, { signal: combinedSignal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const text = await response.text();
      if (!text.includes('#EXTINF')) {
        throw new Error('Response is not a valid M3U (missing #EXTINF)');
      }
      return text;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (lastError.name === 'AbortError') {
        lastError = new Error(`Timeout after 15s via ${label}`);
      }
    } finally {
      clearTimeout(timeout);
    }
  }

  throw lastError ?? new Error('All proxies failed');
}

function combineAbortSignals(
  ...signals: AbortSignal[]
): AbortSignal {
  const controller = new AbortController();
  for (const s of signals) {
    if (s.aborted) {
      controller.abort(s.reason);
      return controller.signal;
    }
    s.addEventListener('abort', () => controller.abort(s.reason), { once: true });
  }
  return controller.signal;
}

export const M3U_SPORTS_URL = 'https://iptv-org.github.io/iptv/categories/sports.m3u';
export const M3U_ALL_URL = 'https://iptv-org.github.io/iptv/index.m3u';
